import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, Check, CreditCard, QrCode, AlertTriangle, Info, Trash2, X } from 'lucide-react';

const typeIcons = {
  pix_gerado: QrCode,
  pix_gestor: QrCode,
  pagamento_confirmado: CreditCard,
  bloqueio: AlertTriangle,
};

export default function NotificationBell() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const prefix = user?.role === 'admin' ? '/admin' : user?.role === 'aluno' ? '/aluno' : '/gestor';

  const fetchCount = useCallback(async () => {
    try {
      const res = await api.get(`${prefix}/notificacoes/count`);
      setUnreadCount(res.data.count);
    } catch {}
  }, [prefix]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${prefix}/notificacoes`);
      setNotifications(res.data);
    } catch {}
    setLoading(false);
  }, [prefix]);

  useEffect(() => { fetchCount(); }, [fetchCount, location.pathname]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`${prefix}/notificacoes/${id}/lida`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.lida);
    for (const n of unread) {
      try { await api.put(`${prefix}/notificacoes/${n.id}/lida`); } catch {}
    }
    setNotifications(prev => prev.map(n => ({ ...n, lida: true })));
    setUnreadCount(0);
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`${prefix}/notificacoes/${id}`);

      setNotifications(prev =>
        prev.filter(n => n.id !== id)
      );

      const removida = notifications.find(n => n.id === id);

      if (removida && !removida.lida) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {
      window.alert('Não foi possível excluir a notificação.');
    }
  };

  const clearNotifications = async () => {
    const confirmado = window.confirm(
      'Tem certeza que deseja excluir todas as notificações?\n\nEssa ação não poderá ser desfeita.'
    );

    if (!confirmado) return;

    try {
      await api.delete(`${prefix}/notificacoes/todas`);
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      window.alert('Não foi possível limpar as notificações.');
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        className="relative text-zinc-400 hover:text-white"
        onClick={() => setOpen(!open)}
        data-testid="notifications-button"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <Badge className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 text-[10px] bg-[#FF3B30] text-white border-0" data-testid="notif-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[#141414] border border-[#27272A] rounded-lg shadow-xl z-50" data-testid="notifications-dropdown">
          <div className="px-4 py-3 border-b border-[#27272A]">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold font-['Barlow_Condensed'] tracking-tight uppercase text-white">
                Notificações
              </p>

              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-[#007AFF] hover:text-blue-300 h-auto py-1 px-2"
                    onClick={markAllRead}
                    data-testid="mark-all-read-btn"
                    title="Marcar todas como lidas"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Ler todas
                  </Button>
                )}

                {notifications.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-red-400 hover:text-red-300 h-auto py-1 px-2"
                    onClick={clearNotifications}
                    data-testid="clear-notifications-btn"
                    title="Excluir todas as notificações"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Limpar
                  </Button>
                )}
              </div>
            </div>

            {notifications.length > 0 && (
              <p className="text-[10px] text-zinc-600 mt-1">
                {notifications.length} notificação
                {notifications.length === 1 ? '' : 'ões'} no histórico
              </p>
            )}
          </div>
          <ScrollArea className="max-h-80">
            {loading ? (
              <div className="p-4 text-center text-zinc-500 text-sm">Carregando...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">Sem notificacoes</p>
              </div>
            ) : (
              <div>
                {notifications.map(n => {
                  const Icon = typeIcons[n.tipo] || Info;
                  return (
                    <div
                      key={n.id}
                      className={`flex items-start gap-3 px-4 py-3 border-b border-[#27272A] last:border-0 cursor-pointer transition-colors ${n.lida ? 'opacity-60' : 'bg-[#007AFF]/5 hover:bg-[#007AFF]/10'}`}
                      onClick={() => !n.lida && markRead(n.id)}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className="w-7 h-7 rounded-md bg-[#007AFF]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="w-3.5 h-3.5 text-[#007AFF]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-bold text-white truncate">{n.titulo}</p>
                          <span className="text-[10px] text-zinc-600 shrink-0">{timeAgo(n.created_at)}</span>
                        </div>
                        <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.mensagem}</p>
                      </div>
                      {!n.lida && (
                        <div className="w-2 h-2 rounded-full bg-[#007AFF] shrink-0 mt-2" />
                      )}

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6 shrink-0 text-zinc-600 hover:text-red-400 hover:bg-red-500/10"
                        title="Excluir notificação"
                        data-testid={`delete-notification-${n.id}`}
                        onClick={(e) => {
                          e.stopPropagation();

                          const confirmado = window.confirm(
                            'Excluir esta notificação?'
                          );

                          if (confirmado) {
                            deleteNotification(n.id);
                          }
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
